import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { login, signup, logout as logoutAPI } from "../services/authService";
import {
  fetchMyPolicies,
  MyPoliciesResponse,
} from "../services/permissionsService";
import authStore from "./authStore";
import { broadcastAuthEvent } from "../utils/authBroadcast";
import { extractSessionId, extractUserId } from "../utils/jwtUtils";

export interface AllowedPolicy {
  entity: string;
  action: string;
}

// Define the shape of the authentication context
interface AuthContextProps {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  userInfo: any | null;
  roles: { id: number; name: string }[];
  isAdmin: boolean;
  allowedPolicies: AllowedPolicy[];
  isPermissionsLoading: boolean;
  hasPermission: (entity: string, action: string) => boolean;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setTokens: (accessToken: string | null, refreshToken: string | null) => void;
  setUserInfo: (userInfo: any) => void;
  signin: (payload: any) => Promise<any>;
  logout: () => void;
  register: (payload: any) => Promise<any>;
}

// Create the authentication context with an initial state
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

// Define a custom hook to access the authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Define the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(
    authStore.getToken()
  );
  const [refreshToken, setRefreshTokenState] = useState<string | null>(
    authStore.getRefreshToken()
  );
  const [userInfo, setUserInfo] = useState<any | null>(null);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [allowedPolicies, setAllowedPolicies] = useState<AllowedPolicy[]>([]);
  const [isPermissionsLoading, setIsPermissionsLoading] =
    useState<boolean>(false);

  const isAuthenticated = !!accessToken;

  // Keep global store in sync for non-React modules
  useEffect(() => {
    const unsubscribe = authStore.subscribe((t) => setAccessToken(t));
    return unsubscribe;
  }, []);

  const hydrateFromStorage = () => {
    const token = localStorage.getItem("token");
    const raw = localStorage.getItem("userInfo");

    // Always sync token from localStorage
    if (token && token !== accessToken) {
      setAccessToken(token);
    } else if (!token && accessToken) {
      setAccessToken(null);
    }

    // Always sync userInfo from localStorage
    if (raw) {
      try {
        const parsedUserInfo = JSON.parse(raw);
        // Only update if the userInfo has actually changed to avoid unnecessary re-renders
        setUserInfo((prevUserInfo: any) => {
          if (JSON.stringify(prevUserInfo) !== JSON.stringify(parsedUserInfo)) {
            return parsedUserInfo;
          }
          return prevUserInfo;
        });
      } catch {
        /* ignore */
      }
    } else if (userInfo) {
      // If localStorage userInfo is cleared but we still have userInfo in state, clear it
      setUserInfo(null);
    }
  };

  useEffect(() => {
    hydrateFromStorage();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token" || e.key === "userInfo") {
        hydrateFromStorage();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Fetch permissions whenever we get/set a token
  useEffect(() => {
    const loadPermissions = async () => {
      if (!accessToken) {
        setAllowedPolicies([]);
        setRoles([]);
        setIsAdmin(false);
        setIsPermissionsLoading(false);
        return;
      }

      setIsPermissionsLoading(true);
      try {
        const resp: MyPoliciesResponse = await fetchMyPolicies();
        setRoles(resp.roles || []);
        setIsAdmin(!!resp.is_admin);
        // Only keep permissions that exist in our config
        const { PERMISSION_ENTITIES } = await import(
          "../config/permissionsConfig"
        );
        const valid = new Set<string>();
        PERMISSION_ENTITIES.forEach((ent) =>
          ent.actions.forEach((a) => valid.add(`${ent.key}:${a.key}`))
        );
        const filtered = (resp.allowed_policies || []).filter((p) =>
          valid.has(`${p.entity}:${p.action}`)
        );
        setAllowedPolicies(filtered);
      } catch (e) {
        // Fail closed: clear permissions if API fails
        setAllowedPolicies([]);
      } finally {
        setIsPermissionsLoading(false);
      }
    };
    loadPermissions();
  }, [accessToken]);

  const hasPermission = useMemo(() => {
    const set = new Set(
      (allowedPolicies || []).map((p) => `${p.entity}:${p.action}`)
    );
    return (entity: string, action: string) =>
      set.has(`${entity}:${action}`) || isAdmin;
  }, [allowedPolicies, isAdmin]);

  const setToken = (token: string | null) => {
    authStore.setToken(token);
    setAccessToken(token);

    // Broadcast authentication state change to other tabs
    if (token) {
      console.log("🔐 Broadcasting logged-in event to other tabs");
      broadcastAuthEvent("logged-in");
    } else {
      console.log("🔐 Broadcasting logged-out event to other tabs");
      broadcastAuthEvent("logged-out");
    }
  };

  const setRefreshToken = (refreshToken: string | null) => {
    authStore.setRefreshToken(refreshToken);
    setRefreshTokenState(refreshToken);
  };

  const setTokens = (
    accessToken: string | null,
    refreshToken: string | null
  ) => {
    authStore.setTokens(accessToken, refreshToken);
    setAccessToken(accessToken);
    setRefreshTokenState(refreshToken);

    // Broadcast authentication state change to other tabs
    if (accessToken) {
      console.log("🔐 Broadcasting logged-in event to other tabs");
      broadcastAuthEvent("logged-in");
    } else {
      console.log("🔐 Broadcasting logged-out event to other tabs");
      broadcastAuthEvent("logged-out");
    }
  };

  const signin = async (payload: any) => {
    const response: any = await login(payload);
    if (response?.access_token) {
      // Handle both access token and refresh token
      const accessToken = response.access_token;
      const refreshToken = response.refresh_token || null;

      setTokens(accessToken, refreshToken);

      if (response?.user_info) {
        // Store userInfo in both localStorage and context state
        localStorage.setItem("userInfo", JSON.stringify(response.user_info));
        localStorage.setItem("userId", response.user_info.id);
        setUserInfo(response.user_info);
      }
    }
    return response;
  };

  const register = async (payload: any) => {
    const response: any = await signup({
      ...payload,
      // user_role_id: user_role_id,
    });
    return response;
  };

  const logout = async () => {
    try {
      // Extract session_id and user_id from current token for logout API
      const currentToken = accessToken;
      if (currentToken) {
        const sessionId = extractSessionId(currentToken);
        const userId = extractUserId(currentToken);

        if (sessionId && userId) {
          console.log("🔐 Calling logout API...");
          await logoutAPI(sessionId, userId);
          console.log("✅ Logout API call successful");
        } else {
          console.warn(
            "⚠️ Could not extract session_id or user_id from token for logout API"
          );
        }
        //empty the navigationHistory
        sessionStorage.setItem("navigationHistory", JSON.stringify([]));
      }
    } catch (error) {
      console.error("❌ Logout API call failed:", error);
      // Continue with local cleanup even if API call fails
    }

    // Clear runtime state in context and broadcast to other tabs
    console.log("🔐 Logout called - broadcasting session-expired event");
    broadcastAuthEvent("session-expired");
    setTokens(null, null);
    setUserInfo(null);
    setAllowedPolicies([]);
    setRoles([]);
    setIsAdmin(false);
  };

  const contextValue: AuthContextProps = {
    isAuthenticated,
    accessToken,
    refreshToken,
    userInfo,
    roles,
    isAdmin,
    allowedPolicies,
    isPermissionsLoading,
    hasPermission,
    setToken,
    setRefreshToken,
    setTokens,
    setUserInfo,
    signin,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
